import type { RumErrorEvent } from "@datadog/browser-rum";
import { datadogRum } from "@datadog/browser-rum";
import { reactPlugin } from "@datadog/browser-rum-react";
import { ErrorInfo as AblyErrorInfo } from "ably";

import { FetchError } from "common/fetch/errors";

import { getErrorTrackingUserData } from "./initialization";

/**
 * Constant indicating whether Datadog RUM should be initialized.
 * Evaluated once at module load time.
 *
 * NOTE: This is only used internally by initDatadogRum(). Consumer code should
 * NOT import this constant - instead, call Datadog APIs directly. They fail
 * silently when RUM is not initialized, which is the intended behavior.
 */
export const USE_DATADOG = !!(
  typeof window !== "undefined" &&
  window.USE_DATADOG_RUM &&
  window.DATADOG_RUM_APPLICATION_ID &&
  window.DATADOG_RUM_CLIENT_TOKEN
);

const isNoiseError = (event: RumErrorEvent, error: unknown): boolean => {
  const message = event.error?.message || "";
  const stack = event.error?.stack || "";
  const errorType = event.error?.type || "";

  // Browser extension scripts are outside our control and are not actionable
  if (
    stack.includes("chrome-extension://") ||
    stack.includes("moz-extension://") ||
    stack.includes("safari-extension://") ||
    stack.includes("safari-web-extension://")
  ) {
    return true;
  }

  if (
    stack.includes("googletagmanager.com") ||
    stack.includes("cdn.popt.in") ||
    stack.includes("file://") ||
    stack.includes("@webkit-masked-url")
  ) {
    return true;
  }

  if (
    message.includes("Object Not Found Matching Id") ||
    message === "undefined" ||
    message.includes("ResizeObserver loop completed")
  ) {
    return true;
  }

  // Browser extension runtime/messaging noise injected into the page
  if (
    message.includes("runtime.sendMessage(). Tab not found") ||
    message.includes("No Listener: tabs:outgoing.message.ready") ||
    message.includes("Failed to initialize messaging: tx_attempts_exceeded") ||
    message.includes("_AutofillCallbackHandler") ||
    message.includes("SymBrowser_Modify") ||
    message.includes("__firefox__") ||
    message.includes("fixinatorInputs") ||
    message.includes("WKWebView API client did not respond to this postMessage")
  ) {
    return true;
  }

  // DuckDuckGo browser injects a content-scope-script whose messaging layer throws
  // "feature named `<name>` was not found" (e.g. `pageContext`) when it references a
  // privacy/AI feature that isn't registered on the page. It comes from the browser's
  // injected script, not our bundle, and is only observed in DuckDuckGo. We never throw
  // this shape ourselves, so treat any "feature named ... was not found" as noise.
  if (message.includes("feature named") && message.includes("was not found")) {
    return true;
  }

  // Firefox-only "Property 'handleEvent' is not callable." — thrown from inside
  // Firefox's event-dispatch internals (empty stack, unhandled, scattered across
  // every page) when an object registered via addEventListener has a non-callable
  // handleEvent. We register no object-style listeners, so this comes from browser
  // extensions / injected third-party scripts and is not actionable in app code.
  if (message.includes("Property 'handleEvent' is not callable")) {
    return true;
  }

  // Cross-origin frame access from embedded contexts (third-party scripts,
  // extensions, embedded frames) — not actionable in app code.
  //   - Chrome:  "Blocked a frame with origin ..."
  //   - Firefox: "Permission denied to access property \"dispatchEvent\" on cross-origin object"
  if (
    message.includes("Failed to read a named property 'toJSON' from 'Window'") ||
    message.includes("Blocked a frame with origin") ||
    message.includes("cross-origin object")
  ) {
    return true;
  }

  // Ably WebSocket lifecycle events — not actionable app errors
  const isAblyError = error instanceof AblyErrorInfo || stack.includes("node_modules/ably");
  if (
    isAblyError &&
    (message.includes("Channel detached") ||
      message.includes("Connection to server") ||
      message.includes("Connection failed or disconnected by server") ||
      message.includes("Connection clos") ||
      message.includes("mismatched clientId for existing connection") ||
      message.includes("Unable to connect (network unreachable)") ||
      message.includes("Channel attach timed out") ||
      message.includes("Channel operation failed as channel state is suspended") ||
      message.includes("Presence state is out of sync") ||
      message.includes("Unable to publish message; per-connection publish Rate limit exceeded") ||
      message.includes("Could not find path: /comet/") ||
      message.includes("Client has been destroyed") ||
      message.includes("Empty reason"))
  ) {
    return true;
  }

  // Cross-origin script errors with zero diagnostic value
  if (message.toLowerCase().includes("script error")) {
    return true;
  }

  // CSP violation reports — addressed at the policy level, not app code
  if (errorType.endsWith("-src") || message.includes("csp_violation")) {
    return true;
  }

  // Mapbox touchmove intervention on mobile — known library issue, not actionable
  if (errorType === "IgnoredEventCancel") {
    return true;
  }

  // Mapbox network/tile load failures — environmental, non-actionable
  if (
    stack.includes("mapbox-gl") &&
    (message.includes("Load failed") ||
      message.includes("Failed to fetch") ||
      message.includes("NetworkError when attempting to fetch") ||
      message.includes("Cannot read properties of undefined (reading 'send')"))
  ) {
    return true;
  }

  // Mapbox tile decode failures — mapbox-gl logs a handled console error when
  // window.createImageBitmap() rejects while decoding a tile blob. Observed almost
  // exclusively on mobile Safari when the tab is backgrounded; non-fatal and outside
  // our control. The raw (minified) stack does not contain "mapbox-gl" in production
  // — that only appears in the sourcemap-resolved error.file, which isn't available
  // here — so we scope this to the full, mapbox-specific wrapper message instead.
  if (
    message.includes("Could not load image because of") &&
    message.includes("Note that SVGs are not supported")
  ) {
    return true;
  }

  // Third-party analytics SDK network failures (Amplitude, Segment, etc.)
  if (
    message.includes("Amplitude Logger") ||
    stack.includes("cdn.amplitude.com") ||
    message.includes("[analytics.js] Failed to load Analytics.js") ||
    message.includes("Error sending segment performance metrics") ||
    message.includes("Can't find variable: gmo")
  ) {
    return true;
  }

  // Third-party storefront scripts injected by external integrations (non-vetcove bundles)
  if (
    message.includes("Error in discount_upto:") ||
    message.includes("Error in first_time_subscription_price:")
  ) {
    return true;
  }

  // react-query "No QueryClient set" — thrown when our client bundle runs outside its
  // normal provider tree (clinic marketing embeds, the /challenge interstitial, scraped/
  // saved page copies), almost always by bots/crawlers. It is caught by an error boundary
  // and never occurs on real shop flows, so it is not actionable.
  if (message.includes("No QueryClient set")) {
    return true;
  }

  return false;
};

/**
 * Initialize Datadog RUM.
 * Call this during app initialization.
 */
export const initDatadogRum = (): void => {
  if (!USE_DATADOG) {
    return;
  }

  datadogRum.init({
    applicationId: window.DATADOG_RUM_APPLICATION_ID,
    clientToken: window.DATADOG_RUM_CLIENT_TOKEN,
    site: window.DD_SITE,
    service: window.DD_SERVICE,
    env: window.SETTINGS,
    version: RELEASE_VERSION,
    sessionSampleRate: window.DATADOG_RUM_SESSION_SAMPLE_RATE,
    sessionReplaySampleRate: window.DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE,
    defaultPrivacyLevel: "mask-user-input",
    allowedTracingUrls: [
      {
        match: /^https:\/\/.*\.vetcove\.com\/api/,
        propagatorTypes: ["tracecontext", "datadog"],
      },
    ],
    // React plugin for component tracking
    // NOTE: Currently using router: false because we don't have a standard router implementation
    // TODO: Consider implementing custom router logic to normalize route patterns for better grouping
    // Examples: /orders/123/items/456 -> /orders/:id/items/:id
    // This would improve performance monitoring and error grouping in Datadog
    plugins: [reactPlugin({ router: false })],
    beforeSend: (event, context) => {
      if (event.type === "error") {
        if ("error" in context && context.error instanceof FetchError) {
          return false;
        }
        const error = "error" in context ? context.error : undefined;
        return !isNoiseError(event, error);
      }

      return true;
    },
  });

  // Set user context for Datadog RUM
  const userData = getErrorTrackingUserData();
  if (userData) {
    datadogRum.setUser({
      id: `${userData.id}`,
      email: userData.email,
    });
  }
};
